/// HeatmapControls: Control bar for heatmap mode selection.
/// Provides radio buttons for Off/Frequency/Duration/Error Rate modes
/// and sends heatmap data to the editor worker for visualization.
///
/// Phase 20, Plan 03.

import { useCallback } from "react";
import { useDebugStore } from "@/stores/debugStore";
import { getWorkerManager } from "@/lib/WorkerManager";
import type { HeatmapMode } from "@/types/debug";

// ---------------------------------------------------------------------------
// Mode options
// ---------------------------------------------------------------------------

const HEATMAP_MODES: { value: HeatmapMode; label: string }[] = [
  { value: "off", label: "Off" },
  { value: "frequency", label: "Frequency" },
  { value: "duration", label: "Duration" },
  { value: "errorRate", label: "Error Rate" },
];

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function HeatmapControls() {
  const mode = useDebugStore((s) => s.mode);
  const heatmapMode = useDebugStore((s) => s.heatmapMode);
  const elementTimingData = useDebugStore((s) => s.elementTimingData);
  const setHeatmapMode = useDebugStore((s) => s.setHeatmapMode);

  const isDisabled = mode === "idle" || elementTimingData.size === 0;

  const handleModeChange = useCallback(
    (newMode: HeatmapMode) => {
      setHeatmapMode(newMode);

      // Send heatmap data to editor worker
      const manager = getWorkerManager();
      const editorWorkerId = manager.getWorkerIdByRole("bpmn-editor");
      if (!editorWorkerId) return;

      if (newMode === "off") {
        manager
          .sendCommand(
            editorWorkerId,
            {
              type: "debugOverlay" as const,
              direction: "cmd" as const,
              subCommand: "clearHeatmap",
              payload: {},
            } as never,
            5000,
          )
          .catch(() => {
            // Best-effort
          });
      } else {
        // Compute and send heatmap data
        const updatedData = useDebugStore.getState().heatmapData;
        const heatmapJson: Record<string, number> = {};
        for (const [elementId, value] of updatedData) {
          heatmapJson[elementId] = value;
        }

        manager
          .sendCommand(
            editorWorkerId,
            {
              type: "debugOverlay" as const,
              direction: "cmd" as const,
              subCommand: "applyHeatmap",
              payload: {
                heatmapData: heatmapJson,
              },
            } as never,
            5000,
          )
          .catch(() => {
            // Best-effort
          });
      }
    },
    [setHeatmapMode],
  );

  return (
    <div className="flex items-center gap-3 border-t border-zinc-700 px-3 py-1.5 shrink-0">
      <span className="text-[10px] font-medium text-zinc-500 uppercase tracking-wider">
        Heatmap
      </span>

      {/* Mode selector */}
      <div className="flex gap-0.5 rounded bg-zinc-900 p-0.5">
        {HEATMAP_MODES.map((opt) => (
          <button
            key={opt.value}
            type="button"
            disabled={isDisabled && opt.value !== "off"}
            onClick={() => handleModeChange(opt.value)}
            className={[
              "px-2 py-0.5 text-xs rounded transition-colors",
              heatmapMode === opt.value
                ? "bg-zinc-700 text-zinc-200"
                : "text-zinc-500 hover:text-zinc-300",
              isDisabled && opt.value !== "off" ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Color legend */}
      {heatmapMode !== "off" && (
        <div className="flex items-center gap-1 ml-2">
          <span className="text-[10px] text-zinc-500">Low</span>
          <div className="flex gap-px">
            <div className="w-3 h-2 rounded-sm bg-green-600" />
            <div className="w-3 h-2 rounded-sm bg-yellow-500" />
            <div className="w-3 h-2 rounded-sm bg-red-500" />
          </div>
          <span className="text-[10px] text-zinc-500">High</span>
        </div>
      )}
    </div>
  );
}
